import { z } from "zod";

// GeoJSON types for Mapbox overlay
export const RiskZoneSchema = z.object({
	/** Unique identifier for the risk zone */
	id: z.string(),
	/** Center coordinates [lng, lat] */
	center: z.array(z.number()).describe("Coordinates as [longitude, latitude]"),
	/** Radius in nautical miles */
	radiusNm: z.number(),
	/** Risk level 0-100 */
	risk: z.number().min(0).max(100),
	/** Short reasoning for the risk */
	reasoning: z.string(),
	/** Category of risk */
	category: z.enum([
		"weather",
		"terrain",
		"airspace",
		"notam",
		"density-altitude",
		"visibility",
		"wind",
		"icing",
		"thunderstorm",
		"combined",
	]),
	/** Source data that contributed to this risk zone */
	sources: z.array(z.string()),
});

export const RotorWingRiskOutputSchema = z.object({
	/** Array of risk zones to display on the map */
	zones: z.array(RiskZoneSchema),
	/** Overall flight risk assessment */
	overallRisk: z.number().min(0).max(100),
	/** Summary of the risk assessment */
	summary: z.string(),
	/** Recommendations for the pilot */
	recommendations: z.array(z.string()),
});

export type RiskZone = z.infer<typeof RiskZoneSchema>;
export type RotorWingRiskOutput = z.infer<typeof RotorWingRiskOutputSchema>;

export const RotorWingZoneExposureSchema = z.object({
	zoneIndex: z.number().int().nonnegative(),
	zoneId: z.string(),
	closestDistanceNm: z.number().nonnegative(),
	closestCenterDistanceNm: z.number().nonnegative(),
	closestAt: z.string(),
	durationSeconds: z.number().nonnegative(),
	flightProportion: z.number().min(0).max(1),
	/** Recorded feet; the mean is time-weighted over intervals with altitude at both ends. */
	altitude: z.object({
		minFeet: z.number(),
		maxFeet: z.number(),
		meanFeet: z.number(),
		coveredDurationSeconds: z.number().positive(),
	}).nullable().optional(),
	visits: z.array(z.object({
		enteredAt: z.string(),
		exitedAt: z.string(),
		durationSeconds: z.number().nonnegative(),
	})),
});

export const RotorWingExposureSchema = z.object({
	departureTime: z.string(),
	arrivalTime: z.string(),
	flightDurationSeconds: z.number().positive(),
	/** Distance along the available, interpolated track; not a filed-route distance. */
	flightDistanceNm: z.number().nonnegative(),
	trackPointCount: z.number().int().nonnegative(),
	discardedTrackPointCount: z.number().int().nonnegative(),
	averageTrackIntervalSeconds: z.number().nonnegative(),
	maxTrackIntervalSeconds: z.number().nonnegative(),
	trackStartTime: z.string(),
	trackEndTime: z.string(),
	trackDurationSeconds: z.number().nonnegative(),
	untrackedDurationSeconds: z.number().nonnegative(),
	/** Null when the captured map has no usable center. */
	outsideMapDurationSeconds: z.number().nonnegative().nullable(),
	zones: z.array(RotorWingZoneExposureSchema),
});

export const RotorWingActualRiskOutputSchema = z.object({
	actualRisk: z.number().min(0).max(100),
	summary: z.string().min(1),
	riskFactors: z.array(z.string()).describe("Zone encounters that raised this flight's actual risk score. Do not mention avoided hazards. If the flight avoided all or most risk zones, list general contributing conditions from the input data instead."),
	limitations: z.array(z.string()),
});

export const RotorWingActualRiskDataSchema = RotorWingActualRiskOutputSchema.extend({
	version: z.literal(1),
	inputHash: z.string(),
	analyzedAt: z.string(),
	model: z.string(),
	trackProvider: z.string(),
	snapshotId: z.string(),
	snapshotTimestamp: z.string(),
	snapshotCreatedAt: z.string(),
	exposure: RotorWingExposureSchema,
});

export type RotorWingZoneExposure = z.infer<typeof RotorWingZoneExposureSchema>;
export type RotorWingExposure = z.infer<typeof RotorWingExposureSchema>;
export type RotorWingActualRiskOutput = z.infer<typeof RotorWingActualRiskOutputSchema>;
export type RotorWingActualRiskData = z.infer<typeof RotorWingActualRiskDataSchema>;

/** New clients also tolerate older servers/rows that have no actual-analysis fields. */
export function getRotorWingActualRisk (record: {
	actualRisk?: number | null;
	actualRiskData?: unknown;
} | null | undefined): RotorWingActualRiskData | null {
	if (record?.actualRisk == null) {
		return null;
	}
	const parsed = RotorWingActualRiskDataSchema.safeParse(record.actualRiskData);
	return parsed.success ? parsed.data : null;
}

export type RotorWingActualRiskInput = {
	riskData: RotorWingRiskOutput;
	snapshotTimestamp: string;
	snapshotCreatedAt: string;
	trackProvider: string;
	exposure: RotorWingExposure;
};
